import React, { useEffect, useState } from "react";
import { useCallback, useMemo } from "react";
import { Column, TableInstance } from "react-table";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IDealer } from "../../models/Dealer";
import { ReactTable } from "../../components/table/ReactTable";
import { apiStore } from "../../api/ApiFactory";
import { formatDate } from "../../helpers/utils/formatDate";
import { useAuth } from "../../store/hooks/useAuth";
import { Roles } from "../../helpers/enums/AuthConstants";
import { getValueByKey } from "../../helpers/utils/getEnumValueKey";
import { DefaultCostType } from "../../helpers/enums/Common";
import { SelectColumnFilter } from "../../components/table/filters/SelectColumnFilter";
import { Checkbox, Chip, Grid } from "@mui/material";
import { NameValueEntity } from "../../models/Common";
import CSVExport from "../../components/export/CSVExport";

const DealerTable = () => {
  let navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  let authuser = useAuth().authuser;

  const [exportList, setExportList] = useState<boolean>(false);
  const [exportData, setExportData] = useState<{ headers: string[]; data: Record<string, any>[] }>({
      headers: [],
      data: [],
  });

  const columns: Column<IDealer>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Location Tags",
        accessor: "locationTags",
        width: 100,
        Cell: (cell: any) => {
          return (
            <Grid container gap={1}>
              {cell.value &&
                cell.value.map((item: string, index: number) => {
                  return (
                    <span key={index}>
                      <Chip
                        size="small" 
                        className="cell-chip"
                        label={item}
                      />
                    </span>
                  );
                })}
            </Grid>
          );
        },
      },
      {
        Header: "Default Cost Type",
        accessor: "defaultCostType",
        Cell: (cell: any) =>
          cell.value !== "" ? getValueByKey(cell.value, DefaultCostType) : "",
        width: 80,
        Filter: SelectColumnFilter,
        filterOptions: DefaultCostType.map((item): NameValueEntity => {
                  return {name: item.value, value: item.key}
                })
                
      },
      {
        Header: "Sale Price Mark Up Model",
        accessor: "salePriceMarkupModel",
        Cell: (cell: any) =>
          cell?.value ? Math.trunc(cell?.value * 100) + "%" : "0%",
        width: 70,
      },
      {
        Header: "Sale Price Mark Up Option",
        accessor: "salePriceMarkupOption",
        Cell: (cell: any) =>
          cell?.value ? Math.trunc(cell?.value * 100) + "%" : "0%",
        width: 70,
      },
      {
        Header: "Is Legacy",
        accessor: "isLegacy",
        Filter: SelectColumnFilter,
        Cell: (cell: any) => {
          return (
            <Checkbox
              className="row-checkbox"
              disabled
              checked={cell.value}
              value={cell.value}
            />
          );
        },
        width: 60
      },
      {
        Header: "Available for Leads",
        accessor: "isAvailableForLeads",
        Filter: SelectColumnFilter,
        Cell: (cell: any) => {
          return (
            <Checkbox
              className="row-checkbox"
              disabled
              checked={cell.value}
              value={cell.value}
            />
          );
        },
        width: 80
      },
      {
        Header: "Designation",
        accessor: (row) => {
          return row.designationTypeName
        },
        Filter: SelectColumnFilter
      },
      {
        Header: "Created Date",
        accessor: "createdDate",
        width: 70,
        Cell: (cell: any) => formatDate(cell.value),
      },
    ],
    []
  );
  const AddRow = useCallback(
    (instance: TableInstance<IDealer>) => () => {
      navigate("/dealer/add");
    },
    []
  );

  const ViewRow = useCallback(
    (instance: TableInstance<IDealer>) => () => {
      const dealer = instance.selectedFlatRows[0].original;
      navigate(`/dealer/view/${dealer.id}`);
    },
    []
  );

  useEffect(() => {
    const result = apiStore.dealerApi.getDealerList({includeInactive: true});
    result.then((response) => {
      if (authuser?.user.role == Roles.DEALER) {
        const dealer = response[0];

        if (dealer.id === authuser.user.dealerId) {
          navigate(`/dealer/view/${dealer.id}`);
        }
      }
      setTableData(response);
    });
  }, []);

  const onExport = useCallback(
    (instance: TableInstance<IDealer>) => () => {
        const headers = instance.allColumns
            .filter(column => column.id !== "_selector")
            .map(column => (typeof column.Header === "string" ? column.Header : String(column.id)));

        if(instance.selectedFlatRows.length === 0) {
            const data = instance.filteredFlatRows.map(row => {
              return instance.allColumns.reduce((acc, column) => {
                  if (column.id !== "_selector") {
                      acc[column.Header as string] = row.values[column.id];
                  }
                  return acc;
              }, {} as Record<string, any>);
            });

            setExportData({ headers, data });
        }
        else {
          const data = instance.selectedFlatRows.map(row => {
              return instance.allColumns.reduce((acc, column) => {
                  if (column.id !== "_selector") {
                      acc[column.Header as string] = row.values[column.id];
                  }
                  return acc;
              }, {} as Record<string, any>);
          });

          setExportData({ headers, data });

        }

        setExportList(true);
  },[]);

  const handleExportComplete = () => {
      setExportList(false);
  };

  return (
    <>
      <ReactTable
        name="order-table"
        columns={columns}
        serverOnChange={false}
        data={tableData ? tableData : []}
        isLoading={false}
        initialFilters={[
            { 
              id: "isLegacy", 
              value: false, 
            },
        ]}
        onAdd={AddRow}
        onEdit={ViewRow}
        onExport={onExport}
      />
      {exportList && <CSVExport list={exportData} fileNamePrefix="Dealer_List" onExportComplete={handleExportComplete}  />}
    </>
  );
};

export default DealerTable;
